import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box, Button, IconButton, Paper, Tab, Tabs, Tooltip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DriveFileMoveIcon from '@mui/icons-material/DriveFileMove';
import { deleteTitle, getTitles, getTitleTree } from '../../../../api/endpoints/corporate';
import type { Title, TitleTreeNode } from '../../../../api/types/corporate';
import { ApiErrorAlert } from '../../../../components/ApiErrorAlert';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import {
  DataTable,
  EmptyState,
  PageHeader,
  RowActions,
  StatusChip,
  useDeleteResource,
  type DataTableColumn,
} from '../../../../components/common';
import { TitleFormDialog } from '../TitleFormDialog';
import { TitleReparentDialog } from '../TitleReparentDialog';
import { TitleOrgChartView } from '../OrgChartView';

interface FlatRow {
  row: Title;
  depth: number;
}

/** Parent-linked listeyi derinlik-etiketli, DFS sıralı diziye düzler — girintili düz Table
 *  için (DepartmentListPage'in aynı yardımcısı; modül sınırlarını bağımsız tutmak için
 *  kopyalandı). Yetim satırlar 0 derinlikte eklenir. */
function flattenTree(rows: Title[]): FlatRow[] {
  const byParent = new Map<number | null, Title[]>();
  for (const row of rows) {
    const key = row.parentTitleId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row);
  }
  const result: FlatRow[] = [];
  const visited = new Set<number>();

  function walk(parentId: number | null, depth: number) {
    const children = (byParent.get(parentId) ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'tr'));
    for (const child of children) {
      if (visited.has(child.id)) continue;
      visited.add(child.id);
      result.push({ row: child, depth });
      walk(child.id, depth + 1);
    }
  }
  walk(null, 0);

  for (const row of rows) {
    if (!visited.has(row.id)) {
      visited.add(row.id);
      result.push({ row, depth: 0 });
    }
  }
  return result;
}

function collectDescendantIds(rows: Title[], rootId: number): Set<number> {
  const byParent = new Map<number | null, number[]>();
  for (const row of rows) {
    const key = row.parentTitleId ?? null;
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key)!.push(row.id);
  }
  const result = new Set<number>([rootId]);
  const stack = [rootId];
  while (stack.length > 0) {
    const id = stack.pop() as number;
    for (const childId of byParent.get(id) ?? []) {
      if (!result.has(childId)) {
        result.add(childId);
        stack.push(childId);
      }
    }
  }
  return result;
}

function treeToRows(nodes: TitleTreeNode[], parentId: number | null = null): Title[] {
  const out: Title[] = [];
  for (const node of nodes) {
    out.push({
      id: node.id,
      name: node.name,
      isActive: node.isActive,
      canViewSubordinates: node.canViewSubordinates,
      level: node.level,
      parentTitleId: parentId,
    });
    if (node.children?.length) out.push(...treeToRows(node.children, node.id));
  }
  return out;
}

export default function TitleListPage() {
  const [view, setView] = useState<'list' | 'chart'>('list');
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Title | null>(null);
  const [reparenting, setReparenting] = useState<Title | null>(null);

  const treeQuery = useQuery({
    queryKey: ['corporate', 'titles', 'tree'],
    queryFn: () => getTitleTree(),
    retry: false,
  });
  const listQuery = useQuery({
    queryKey: ['corporate', 'titles'],
    queryFn: () => getTitles(),
    enabled: treeQuery.isError,
  });
  const isError = treeQuery.isError && listQuery.isError;
  const isLoading =
    (treeQuery.isPending && !treeQuery.isError) || (treeQuery.isError && listQuery.isPending);

  const rows: Title[] = useMemo(
    () => (treeQuery.data?.tree ? treeToRows(treeQuery.data.tree) : listQuery.data?.titles ?? []),
    [treeQuery.data, listQuery.data]
  );

  const flattened = useMemo(() => flattenTree(rows), [rows]);
  const options = useMemo(
    () => flattened.map(({ row, depth }) => ({ id: row.id, label: `${'— '.repeat(depth)}${row.name}` })),
    [flattened]
  );
  const excludeIds = useMemo(
    () => (editing || reparenting ? collectDescendantIds(rows, (editing ?? reparenting)!.id) : new Set<number>()),
    [rows, editing, reparenting]
  );

  const del = useDeleteResource<number>({
    mutationFn: (id) => deleteTitle(id),
    invalidateKeys: [['corporate', 'titles']],
    successMessage: 'Unvan silindi.',
  });
  const deletingName = rows.find((r) => r.id === del.target)?.name ?? '';

  const columns: DataTableColumn<FlatRow>[] = [
    { id: 'name', header: 'Unvan Adı', cell: ({ row, depth }) => <Box sx={{ pl: depth * 3 }}>{row.name}</Box> },
    { id: 'level', header: 'Seviye', cell: ({ row }) => row.level ?? '—' },
    { id: 'subs', header: 'Astları Görebilir', cell: ({ row }) => (row.canViewSubordinates ? 'Evet' : 'Hayır') },
    { id: 'status', header: 'Durum', cell: ({ row }) => <StatusChip active={row.isActive} /> },
  ];

  return (
    <Box>
      <PageHeader
        title="Unvanlar"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
            Yeni Unvan
          </Button>
        }
      />

      {isError && <ApiErrorAlert error={listQuery.error ?? treeQuery.error} sx={{ mb: 2 }} />}

      <Tabs value={view} onChange={(_e, value) => setView(value)} sx={{ mb: 2, minHeight: 36 }}>
        <Tab value="list" label="Liste" sx={{ minHeight: 36 }} />
        <Tab value="chart" label="Organizasyon Şeması" sx={{ minHeight: 36 }} />
      </Tabs>

      {view === 'chart' ? (
        <Paper variant="outlined">
          <TitleOrgChartView rows={rows} onNodeClick={(title) => setEditing(title)} />
        </Paper>
      ) : (
        <DataTable
          columns={columns}
          rows={flattened}
          getRowKey={({ row }) => row.id}
          loading={isLoading}
          emptyState={<EmptyState variant="row" colSpan={5} title="Kayıtlı unvan bulunamadı." />}
          rowActions={({ row }) => (
            <RowActions
              onEdit={() => setEditing(row)}
              onDelete={() => del.request(row.id)}
              leading={
                <Tooltip title="Üst Unvanı Değiştir">
                  <IconButton size="small" onClick={() => setReparenting(row)} aria-label="Taşı">
                    <DriveFileMoveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              }
            />
          )}
        />
      )}

      <TitleFormDialog open={createOpen} title={null} options={options} excludeIds={new Set()} onClose={() => setCreateOpen(false)} />
      <TitleFormDialog open={!!editing} title={editing} options={options} excludeIds={excludeIds} onClose={() => setEditing(null)} />
      <TitleReparentDialog open={!!reparenting} title={reparenting} options={options} excludeIds={excludeIds} onClose={() => setReparenting(null)} />

      <ConfirmDialog
        {...del.confirmDialogProps}
        title="Unvanı sil"
        description={`"${deletingName}" unvanını silmek istediğinize emin misiniz?`}
      />
    </Box>
  );
}
